import type React from 'react';
import { Sheet } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type FormDrawerProps = {
    /** The fields. Laid out as a column; the body scrolls, the footer does not. */
    children: React.ReactNode;
    /** The action row — Cancel / Save. Pinned, so it cannot scroll away. */
    footer: React.ReactNode;
    isOpen: boolean;
    /**
     * Optional facts about what the drawer edits, under the subtitle: chips
     * such as a record's status. Laid out as a wrapping row.
     */
    meta?: React.ReactNode;
    onClose: () => void;
    /** Already localized. The line under the title. */
    subtitle: string;
    /** Already localized. Also the dialog's accessible name. */
    title: string;
    /**
     * Literal static prefix for this drawer's chrome — `<testId>` plus
     * `-header`, `-title`, `-description`, `-body` and `-footer`, and `-meta`
     * when `meta` is given.
     *
     * Views normally hardcode their own ids (rulebook §5.4), which now also
     * forbids this prop; `.agents/frontend.md` registers the migration. The
     * original exception was a shared component with more than one instance
     * across more than one page, where a hardcoded id would make every
     * caller's test ambiguous — `PageNotice` and `SuccessNotice` already take
     * exactly this prop for exactly that reason. Only the chrome ids are
     * derived: the fields, the meta's chips and the action buttons are
     * caller-supplied nodes and keep their literal ids in the calling View.
     */
    testId: string;
};

/**
 * The right-side add/edit form drawer.
 *
 * A form belongs beside the list it edits, not on top of it: the drawer leaves
 * the row you were looking at visible, and it has room for a form that is
 * taller than a dialog should be. This is the `Sheet` chain that thirteen
 * drawers hand-repeat, extracted at the point where six more callers wanted it.
 *
 * **The actions are pinned.** They live in `Sheet.Footer`, which is
 * `flex-shrink: 0`, while `Sheet.Body` scrolls. The four accounting register
 * drawers (employee, asset, shareholder, account) instead put Cancel/Save at
 * the end of the body — which works at their field counts and does not work for
 * a form carrying an image cropper and five fields, where Save scrolls out of
 * sight exactly when it is wanted. Those four are the follow-up candidates for
 * this shell; they are not folded in here because each is pinned by tests
 * asserting its own ids.
 *
 * **The subtitle is a sibling of the heading, not inside it.** `Sheet.Heading`
 * is react-aria's `Heading` with `slot="title"` — it renders a real heading
 * element, so a paragraph of body copy nested in one is invalid markup. The
 * sibling form is also what `.sheet__header`'s own column layout is built for,
 * and it is a one-for-one replacement for the `Modal.Header > Modal.Heading +
 * <p>` these drawers grew out of.
 *
 * Nothing here restates HeroUI's own typography: `.sheet__heading` is already
 * `text-lg` at weight 600, and re-specifying that through `className` is the
 * restyling the house rules forbid.
 */
export const FormDrawer: React.FC<FormDrawerProps> = ({
    children,
    footer,
    isOpen,
    meta,
    onClose,
    subtitle,
    testId,
    title,
}) => (
    <Sheet
        isOpen={isOpen}
        onOpenChange={(open) => !open && onClose()}
        placement="right"
    >
        <Sheet.Backdrop>
            {/* Below `sm` the drawer takes the whole screen: the sheet's own
                96vw cap would leave a sliver of the page behind it that is too
                narrow to use and only reads as a mis-sized panel. */}
            <Sheet.Content className="w-full max-sm:max-w-none sm:max-w-[560px]">
                <Sheet.Dialog aria-label={title} {...getNodeTestIdProp(testId)}>
                    <Sheet.CloseTrigger />
                    <Sheet.Header {...getNodeTestIdProp(`${testId}-header`)}>
                        <Sheet.Heading
                            {...getNodeTestIdProp(`${testId}-title`)}
                        >
                            {title}
                        </Sheet.Heading>
                        <p
                            className="text-sm text-muted"
                            {...getNodeTestIdProp(`${testId}-description`)}
                        >
                            {subtitle}
                        </p>
                        {meta === undefined ? null : (
                            <div
                                className="flex flex-wrap items-center gap-2"
                                {...getNodeTestIdProp(`${testId}-meta`)}
                            >
                                {meta}
                            </div>
                        )}
                    </Sheet.Header>
                    {/*
                     * `data-sheet-no-drag` is load-bearing, not decoration.
                     *
                     * A `Sheet` makes its whole content draggable to dismiss,
                     * and for a left- or right-placed one the gesture is
                     * allowed unconditionally — the scroll-position and
                     * text-selection escape hatches in the vendor's own gate
                     * are only reached for top/bottom. A form body is full of
                     * pointer-drag surfaces: `ImageCropUpload`'s crop box moves
                     * and resizes on pointer drag, and dragging it rightward
                     * past a quarter of the drawer's width would dismiss the
                     * drawer and discard the form. Selecting text in a field
                     * is the same bug, smaller.
                     *
                     * This attribute is the vendor's escape hatch, matched with
                     * `closest()`, so one on the body covers every field. The
                     * header, the footer and the backdrop still dismiss.
                     */}
                    <Sheet.Body
                        className="flex flex-col gap-5"
                        data-sheet-no-drag
                        {...getNodeTestIdProp(`${testId}-body`)}
                    >
                        {children}
                    </Sheet.Body>
                    <Sheet.Footer {...getNodeTestIdProp(`${testId}-footer`)}>
                        {footer}
                    </Sheet.Footer>
                </Sheet.Dialog>
            </Sheet.Content>
        </Sheet.Backdrop>
    </Sheet>
);
